import { Id } from 'common/types.typescript';
import qs from 'qs';
import api from '../api';

// Types
export interface Employee {
  custom_id: string;
  email: string;
  first_name: string;
  last_name: string;
  group_id: number;
  id: Id;
  image: string;
}

export interface Interaction {
  id: number;
  interaction_event_ids: Array<number>;
  external_id: string;
  url: string;
  tags: Record<string, unknown>;
  properties: Record<string, unknown>;
}
interface PartialReview {
  completed_at: string | null;
  interaction_id: string;
  reviewer: Employee;
}

export interface Review {
  acknowledged_at: string | null;
  appeal_resolved_at: string | null;
  appealed_at: string | null;
  autofailed: false;
  blind: boolean;
  completed_at: string | null;
  count_toward_score: boolean;
  employee: Employee;
  employee_id: number;
  filename: string | null;
  hide_reviewer: boolean;
  id: number;
  interaction: Interaction;
  interaction_id: string;
  interaction_snapshot_id: number;
  interaction_url: string;
  percentage_score: 0;
  primary_metric_value: number | null;
  qa_allocation_id: number | null;
  qa_annotations_count: number;
  qa_assignment_id: number | null;
  qa_calibration_id: number | null;
  qa_form_id: number;
  qa_form_name: string;
  has_transcript: boolean;
  has_file: boolean;
  resolution_status: string;
  reviewer: Employee;
  solicitation_id: null;
  started_at: string | null;
  transcript: string | null;
}

export interface Calibration {
  alignment_score: string | null;
  completed_at: string | null;
  current_user_qa_review: { id: number };
  employee: Employee;
  id: number;
  meeting_at: string | null;
  notes: string | null;
  qa_reviews: (PartialReview | Review)[];
}

export interface CalibrationsState {
  allCompleted: Calibration[];
  allCompletedLoading: boolean;
  allIncomplete: Calibration[];
  allIncompleteLoading: boolean;
  owned: Calibration[];
  assigned: Calibration[];
  completed: Calibration[];
  completedLoading: boolean;
  loading: boolean;
  doneLoading: boolean;
  totalPages: number;
  page: number;
}

export interface CalibrationsData {
  qa_calibrations: Calibration[];
  total_pages: number;
  page: number;
}

// Selectors

export const calibrationParticipants = (calibration: Calibration) => {
  const startedBy = calibration.employee;
  return calibration.qa_reviews.reduce((a: Employee[], qr) => {
    if (qr.reviewer.id !== startedBy.id) {
      return a.concat([qr.reviewer]);
    }
    return a;
  }, []);
};

enum ActionTypes {
  FETCH_CALIBRATIONS_REQUEST = 'calibrationsPage/FETCH_CALIBRATIONS_REQUEST',
  FETCH_ALL_CALIBRATIONS_REQUEST = 'calibrationsPage/FETCH_ALL_CALIBRATIONS_REQUEST',
  FETCH_OWNED_CALIBRATIONS_SUCCESS = 'calibrationsPage/FETCH_OWNED_CALIBRATIONS_SUCCESS',
  FETCH_ASSIGNED_CALIBRATIONS_SUCCESS = 'calibrationsPage/FETCH_ASSIGNED_CALIBRATIONS_SUCCESS',
  FETCH_COMPLETED_CALIBRATIONS_SUCCESS = 'calibrationsPage/FETCH_COMPLETED_CALIBRATIONS_SUCCESS',
  FETCH_ALL_COMPLETED_CALIBRATIONS_SUCCESS = 'calibrationsPage/FETCH_ALL_COMPLETED_CALIBRATIONS_SUCCESS',
  FETCH_ALL_INCOMPLETE_CALIBRATIONS_SUCCESS = 'calibrationsPage/FETCH_ALL_INCOMPLETE_CALIBRATIONS_SUCCESS',
  SET_COMPLETED_CALIBRATIONS_LOADING = 'calibrationsPage/SET_COMPLETED_CALIBRATIONS_LOADING',
  SET_ALL_COMPLETED_CALIBRATIONS_LOADING = 'calibrationsPage/SET_ALL_COMPLETED_CALIBRATIONS_LOADING',
  SET_ALL_INCOMPLETE_CALIBRATIONS_LOADING = 'calibrationsPage/SET_ALL_INCOMPLETE_CALIBRATIONS_LOADING',
  LOADING_FALSE = 'calibrationsPage/LOADING_FALSE',
}

type FetchCalibrationRequestAction = {
  type: ActionTypes.FETCH_CALIBRATIONS_REQUEST;
};

type FetchOwnedCalibrationSuccess = {
  type: ActionTypes.FETCH_OWNED_CALIBRATIONS_SUCCESS;
  calibrations: Calibration[];
};

type FetchAssignedCalibrationSuccess = {
  type: ActionTypes.FETCH_ASSIGNED_CALIBRATIONS_SUCCESS;
  calibrations: Calibration[];
};

type FetchCompletedCalibrationSuccess = {
  type: ActionTypes.FETCH_COMPLETED_CALIBRATIONS_SUCCESS;
  calibrations: CalibrationsData;
};

type FetchAllIncompleteCalibrationsSuccess = {
  type: ActionTypes.FETCH_ALL_INCOMPLETE_CALIBRATIONS_SUCCESS;
  calibrations: Calibration[];
};

type FetchAllCompletedCalibrationSuccess = {
  type: ActionTypes.FETCH_ALL_COMPLETED_CALIBRATIONS_SUCCESS;
  calibrations: CalibrationsData;
};

type LoadingFalse = {
  type: ActionTypes.LOADING_FALSE;
};

type SetCompletedCalibrationsLoading = {
  type: ActionTypes.SET_COMPLETED_CALIBRATIONS_LOADING;
  loading: boolean;
};

type SetAllCompletedCalibrationsLoading = {
  type: ActionTypes.SET_ALL_COMPLETED_CALIBRATIONS_LOADING;
  loading: boolean;
};

type SetAllIncompletedCalibrationsLoading = {
  type: ActionTypes.SET_ALL_INCOMPLETE_CALIBRATIONS_LOADING;
  loading: boolean;
};

// Action Creators

type CalibrationAction =
  | FetchCalibrationRequestAction
  | FetchOwnedCalibrationSuccess
  | FetchAssignedCalibrationSuccess
  | FetchCompletedCalibrationSuccess
  | FetchAllCompletedCalibrationSuccess
  | FetchAllIncompleteCalibrationsSuccess
  | LoadingFalse
  | SetCompletedCalibrationsLoading
  | SetAllCompletedCalibrationsLoading
  | SetAllIncompletedCalibrationsLoading;

const fetchRequest = (): FetchCalibrationRequestAction => ({
  type: ActionTypes.FETCH_CALIBRATIONS_REQUEST,
});

const fetchOwnedCalibrationsSuccess = (
  calibrations: Calibration[]
): FetchOwnedCalibrationSuccess => ({
  type: ActionTypes.FETCH_OWNED_CALIBRATIONS_SUCCESS,
  calibrations,
});

const fetchAssignedCalibrationsSuccess = (
  calibrations: Calibration[]
): FetchAssignedCalibrationSuccess => ({
  type: ActionTypes.FETCH_ASSIGNED_CALIBRATIONS_SUCCESS,
  calibrations,
});

const fetchCompletedCalibrationsSuccess = (
  calibrations: CalibrationsData
): FetchCompletedCalibrationSuccess => ({
  type: ActionTypes.FETCH_COMPLETED_CALIBRATIONS_SUCCESS,
  calibrations,
});

const fetchAllIncompleteCalibrationsSuccess = (
  calibrations: Calibration[]
): FetchAllIncompleteCalibrationsSuccess => ({
  type: ActionTypes.FETCH_ALL_INCOMPLETE_CALIBRATIONS_SUCCESS,
  calibrations,
});

const fetchAllCompletedCalibrationsSuccess = (
  calibrations: CalibrationsData
): FetchAllCompletedCalibrationSuccess => ({
  type: ActionTypes.FETCH_ALL_COMPLETED_CALIBRATIONS_SUCCESS,
  calibrations,
});

const setCompletedCalibrationsLoading = loading => ({
  type: ActionTypes.SET_COMPLETED_CALIBRATIONS_LOADING,
  loading,
});

const setAllCompletedCalibrationsLoading = loading => ({
  type: ActionTypes.SET_ALL_COMPLETED_CALIBRATIONS_LOADING,
  loading,
});

const setAllIncompletedCalibrationsLoading = loading => ({
  type: ActionTypes.SET_ALL_INCOMPLETE_CALIBRATIONS_LOADING,
  loading,
});

const loadingFalse = (): LoadingFalse => ({
  type: ActionTypes.LOADING_FALSE,
});

export const updatePage = (page: number, location, history) => () => {
  const filters = qs.parse(location.search, { ignoreQueryPrefix: true });
  filters.page = page;
  history.push({
    search: qs.stringify(filters, { addQueryPrefix: true }),
  });
};

export const fetchCalibrations =
  (location, completedStartDate = '', completedEndDate = '') =>
  (dispatch, getState) => {
    const filters = qs.parse(location.search, { ignoreQueryPrefix: true });
    const options = {
      status: 'completed',
      ...(filters.page && { page: filters.page }),
      ...(completedStartDate &&
        completedEndDate && {
          start_date: completedStartDate,
          end_date: completedEndDate,
        }),
    };
    const isPaginationUpdate =
      filters.page && filters.page !== getState().qaCalibrations.page;

    dispatch(fetchRequest());
    if (isPaginationUpdate) {
      api
        .fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`)
        .then(([json]) => {
          dispatch(loadingFalse());
          dispatch(fetchCompletedCalibrationsSuccess(json));
        });
    } else {
      Promise.all([
        api.fetch(dispatch, 'qa_calibrations?status=owned&per=100'),
        api.fetch(dispatch, 'qa_calibrations?status=assigned&per=100'),
        api.fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`),
      ]).then(([[todo], [assigned], [done]]) => {
        dispatch(loadingFalse());
        dispatch(fetchOwnedCalibrationsSuccess(todo.qa_calibrations));
        dispatch(fetchAssignedCalibrationsSuccess(assigned.qa_calibrations));
        dispatch(fetchCompletedCalibrationsSuccess(done));
      });
    }
  };

export const fetchIncompleteCalibrations = () => dispatch => {
  dispatch(fetchRequest());
  Promise.all([
    api.fetch(dispatch, 'qa_calibrations?status=owned&per=100'),
    api.fetch(dispatch, 'qa_calibrations?status=assigned&per=100'),
  ]).then(([[todo], [assigned]]) => {
    dispatch(loadingFalse());
    dispatch(fetchOwnedCalibrationsSuccess(todo.qa_calibrations));
    dispatch(fetchAssignedCalibrationsSuccess(assigned.qa_calibrations));
  });
};

export const fetchCompleteCalibrations = filters => dispatch => {
  const options = {
    status: 'completed',
    ...filters,
  };

  dispatch(setCompletedCalibrationsLoading(true));
  api
    .fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`)
    .then(([completed]) => {
      dispatch(setCompletedCalibrationsLoading(false));
      dispatch(fetchCompletedCalibrationsSuccess(completed));
    });
};

export const fetchAllCalibrations =
  (location, completedStartDate = '', completedEndDate = '') =>
  (dispatch, getState) => {
    const filters = qs.parse(location.search, { ignoreQueryPrefix: true });
    const options = {
      status: 'all-completed',
      ...(filters.page && { page: filters.page }),
      ...(completedStartDate &&
        completedEndDate && {
          start_date: completedStartDate,
          end_date: completedEndDate,
        }),
    };
    const isPaginationUpdate =
      filters.page && filters.page !== getState().qaCalibrations.page;

    dispatch(fetchRequest());
    if (isPaginationUpdate) {
      api
        .fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`)
        .then(([json]) => {
          dispatch(loadingFalse());
          dispatch(fetchAllCompletedCalibrationsSuccess(json));
        });
    } else {
      Promise.all([
        api.fetch(dispatch, 'qa_calibrations?status=all-incomplete&per=100'),
        api.fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`),
      ]).then(([[incomplete], [completed]]) => {
        dispatch(loadingFalse());
        dispatch(fetchAllCompletedCalibrationsSuccess(completed));
        dispatch(
          fetchAllIncompleteCalibrationsSuccess(incomplete.qa_calibrations)
        );
      });
    }
  };

export const fetchAllIncompleteCalibrations = () => dispatch => {
  dispatch(setAllIncompletedCalibrationsLoading(true));

  api
    .fetch(dispatch, 'qa_calibrations?status=all-incomplete&per=100')
    .then(([incomplete]) => {
      dispatch(setAllIncompletedCalibrationsLoading(false));
      dispatch(
        fetchAllIncompleteCalibrationsSuccess(incomplete.qa_calibrations)
      );
    });
};

export const fetchAllCompleteCalibrations = filters => dispatch => {
  const options = {
    status: 'all-completed',
    ...filters,
  };

  dispatch(setAllCompletedCalibrationsLoading(true));
  api
    .fetch(dispatch, `qa_calibrations?${new URLSearchParams(options)}`)
    .then(([completed]) => {
      dispatch(setAllCompletedCalibrationsLoading(false));
      dispatch(fetchAllCompletedCalibrationsSuccess(completed));
    });
};

// Reducers

export const initialState: CalibrationsState = {
  allCompleted: [],
  allCompletedLoading: false,
  allIncomplete: [],
  allIncompleteLoading: false,
  owned: [],
  assigned: [],
  completed: [],
  completedLoading: false,
  loading: false,
  doneLoading: false,
  totalPages: 1,
  page: 1,
};

export const reducer = (state = initialState, action: CalibrationAction) => {
  switch (action.type) {
    case ActionTypes.FETCH_CALIBRATIONS_REQUEST: {
      return { ...state, loading: true };
    }
    case ActionTypes.SET_COMPLETED_CALIBRATIONS_LOADING: {
      return { ...state, completedLoading: action.loading };
    }
    case ActionTypes.SET_ALL_COMPLETED_CALIBRATIONS_LOADING: {
      return { ...state, allCompletedLoading: action.loading };
    }
    case ActionTypes.SET_ALL_INCOMPLETE_CALIBRATIONS_LOADING: {
      return { ...state, allIncompletedLoading: action.loading };
    }
    case ActionTypes.FETCH_OWNED_CALIBRATIONS_SUCCESS: {
      return { ...state, owned: action.calibrations };
    }
    case ActionTypes.FETCH_ASSIGNED_CALIBRATIONS_SUCCESS: {
      return { ...state, assigned: action.calibrations };
    }
    case ActionTypes.FETCH_COMPLETED_CALIBRATIONS_SUCCESS: {
      return {
        ...state,
        completed: action.calibrations.qa_calibrations,
        totalPages: action.calibrations.total_pages,
        page: action.calibrations.page,
      };
    }
    case ActionTypes.FETCH_ALL_INCOMPLETE_CALIBRATIONS_SUCCESS: {
      return { ...state, allIncomplete: action.calibrations };
    }
    case ActionTypes.FETCH_ALL_COMPLETED_CALIBRATIONS_SUCCESS: {
      return {
        ...state,
        allCompleted: action.calibrations.qa_calibrations,
        totalPages: action.calibrations.total_pages,
        page: action.calibrations.page,
      };
    }
    case ActionTypes.LOADING_FALSE: {
      return { ...state, loading: false, doneLoading: false };
    }
    default: {
      return state;
    }
  }
};
